/**
 * Мост к десктоп-оболочке (Electron): глобальный push-to-talk и сочетания клавиш, счётчик на иконке,
 * настройки окна, разрешения macOS и обновления. В браузере объекта нет — все функции превращаются в заглушки.
 */

export interface DesktopSettings {
  overlayEnabled?: boolean
  overlayCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  overlayDisplay?: number | null
  overlayOpacity?: number
  overlayAvatarSize?: 'small' | 'medium' | 'large'
  overlayNames?: 'always' | 'talking' | 'never'
  overlayUsers?: 'always' | 'talking'
  overlayMaxUsers?: number
  overlayAvatars?: boolean
  overlayTags?: boolean
  overlayStreaming?: boolean
  overlayChannel?: boolean
  trayOnClose: boolean
  autostart: boolean
  hardwareAcceleration: boolean
  /** Показывать друзьям запущенную игру. */
  shareGame: boolean
  pttKey: string
  pttAvailable: boolean
}

/** Что происходит с обновлением приложения. */
export interface UpdateState {
  status: 'idle' | 'checking' | 'none' | 'downloading' | 'ready' | 'manual' | 'error'
  current: string
  latest: string
  percent: number
  url: string
  error: string
  supported: boolean
}

/** Экран или окно, которое можно показать. Превью и значок приходят готовыми картинками. */
export interface DesktopSource {
  id: string
  name: string
  thumbnail: string
  appIcon: string | null
}

interface DesktopApi {
  desktop: true
  version: string
  platform: string
  /** Optional so the new web client still runs in older desktop releases. */
  overlay?: {
    widget?: boolean
    update: (state: OverlaySnapshot | null) => void
    preview: () => Promise<void>
    toggle: () => Promise<void>
    displays: () => Promise<{ id: number; label: string }[]>
    onSettings: (listener: (settings: DesktopSettings) => void) => () => void
  }
  getSettings: () => Promise<DesktopSettings>
  setSettings: (patch: Partial<DesktopSettings>) => Promise<DesktopSettings>
  pttAvailable: () => Promise<boolean>
  setPTTKey: (code: string) => Promise<boolean>
  onPTT: (listener: (down: boolean) => void) => () => void
  setBadge: (count: number) => void
  attention: (on: boolean) => void
  onUpdateReady: (listener: (info: { version: string }) => void) => () => void
  installUpdate: () => void
  onUpdateManual?: (listener: (info: { version: string; url: string }) => void) => () => void
  downloadUpdate?: () => void
  // появились в 0.1.10: у старой оболочки их нет, и клиент обходится без них
  updateState?: () => Promise<UpdateState>
  checkUpdate?: () => Promise<UpdateState>
  onUpdateState?: (listener: (state: UpdateState) => void) => () => void
  setTitlebar?: (color: string, symbolColor: string) => void
  // появились в 0.1.12: оболочка узнаёт игру по запущенным процессам
  mediaAccessAtStart?: (kind: 'screen') => Promise<string>
  relaunch?: () => Promise<void>
  currentGame?: () => Promise<{ name: string; steamAppId?: string } | null>
  onGame?: (listener: (game: { name: string; steamAppId?: string } | null) => void) => () => void
  // появилось в 0.1.17: список экранов и окон — выбор источника рисует сам клиент
  sources?: (kind: 'screen' | 'window') => Promise<{ ok: boolean; reason?: string; sources?: DesktopSource[] }>
  pickSource?: (id: string | null) => Promise<void>
  // появилось в 0.1.16: звук компьютера без звука самого Кличата (только Windows 10 2004+)
  systemAudio?: {
    available: () => Promise<boolean>
    start: () => Promise<{ ok: boolean; error?: string; sampleRate?: number; channels?: number }>
    stop: () => Promise<void>
    onData: (listener: (pcm: Uint8Array) => void) => () => void
  }
  // появилось после 0.1.20: сочетания для микрофона и звука в фоне, тем же перехватом, что у рации
  hotkeys?: {
    /** Какие сочетания слушать вне окна; true — перехват работает. Пустой список — перестать. */
    set: (list: GlobalHotkey[]) => Promise<boolean>
    /** ok — работает; permission — macOS не дал «Универсальный доступ»; unavailable — перехвата нет. */
    status: () => Promise<'ok' | 'permission' | 'unavailable'>
    onTrigger: (listener: (id: string) => void) => () => void
    /**
     * Появилось после 0.1.21: оболочка ловит и кнопки мыши (code Mouse1…Mouse5) и присылает их
     * нажатие всегда, а не только из фона. Старая оболочка сочетания на мыши молча пропускает.
     */
    mouse?: boolean
  }
  mediaAccess: (kind: 'screen' | 'microphone' | 'camera') => Promise<string>
  openPrivacySettings: (kind: 'screen' | 'microphone' | 'camera' | 'accessibility') => void
}

export interface OverlaySnapshot {
  channel: string
  status: 'connecting' | 'connected' | 'reconnecting'
  participants: {
    id: string; name: string; avatar: string | null
    speaking: boolean; muted: boolean; deafened: boolean; self: boolean
    tag: string | null; tagColor: number | null; streaming: boolean
  }[]
}

/** Сочетание для глобального перехвата: код клавиши как у KeyboardEvent.code (или Mouse1…Mouse5) и модификаторы. */
export interface GlobalHotkey {
  id: string
  code: string
  ctrl: boolean
  alt: boolean
  shift: boolean
  meta: boolean
}

declare global {
  interface Window {
    klichat?: DesktopApi
  }
}

/** Где лежат установщики: одни и те же ссылки на лендинге и в настройках. */
export const DOWNLOADS = {
  base: 'https://storage.yandexcloud.net/klichat-desktop',
  windows: 'https://storage.yandexcloud.net/klichat-desktop/klichat-win-setup.exe',
  mac: 'https://storage.yandexcloud.net/klichat-desktop/klichat-mac-arm64.dmg',
  macIntel: 'https://storage.yandexcloud.net/klichat-desktop/klichat-mac-x64.dmg',
}

export const desktop: DesktopApi | null = typeof window !== 'undefined' && window.klichat ? window.klichat : null
export const isDesktop = desktop !== null
/** Названия ОС для текстов и ссылок на скачивание. */
export const desktopPlatform = desktop?.platform ?? ''

/**
 * Что предложить скачать из браузера: Windows или macOS. В самом приложении, на телефоне и
 * на Linux — ничего: там либо уже приложение, либо сборки нет.
 */
export function browserDownload(): { label: string; url: string; alt?: { label: string; url: string } } | null {
  if (isDesktop || typeof navigator === 'undefined') return null
  const ua = navigator.userAgent
  if (/Android|iPhone|iPad|Mobile/i.test(ua)) return null
  if (/Windows/i.test(ua)) return { label: 'Скачать для Windows', url: DOWNLOADS.windows }
  if (/Mac OS X|Macintosh/i.test(ua)) return { label: 'Скачать для macOS', url: DOWNLOADS.mac, alt: { label: 'версия для Intel', url: DOWNLOADS.macIntel } }
  return null
}
